import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOrgContext } from '@/hooks/useOrgContext';
import { UserPlus, ClipboardCheck, HeartPulse, Clock, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { careTeamLine, ASC_FACILITY_ID, type OrCase, type RoomResource, type RoomStay } from './schedule/useAscSchedule';

// ── Stage pages (rev 2, Wayne review 2026-08-15) ────────────────────────────
// Patient Access, Pre-Op, PACU as full working surfaces, not bare lists:
// date navigation (day pager; Patient Access adds a week view), provider
// filter, the stage's BAYS with live occupants, and the schedule of cases
// relevant to the stage — each case card carrying its care team
// (anesthesiologist, circulating RN, scrub tech) from cr.or_cases.
// Area-level staffing (who is staffing pre-op today) needs a staffing table
// that does not exist yet — tracked as backlog, not faked.

type BoardKind = 'access' | 'preop' | 'pacu';

const CFG: Record<BoardKind, {
    title: string; subtitle: string; icon: ReactNode;
    bayKind: RoomResource['resource_kind'] | null;
    stages: string[]; weekToggle: boolean; emptyCopy: string;
}> = {
    access: {
        title: 'Patient Access',
        subtitle: 'Arrivals and registration — scheduled cases by day or week',
        icon: <UserPlus className="w-5 h-5" />,
        bayKind: null, stages: [], weekToggle: true,
        emptyCopy: 'No cases scheduled in this window.',
    },
    preop: {
        title: 'Pre-Op',
        subtitle: 'Pre-operative bays and the upcoming case schedule',
        icon: <ClipboardCheck className="w-5 h-5" />,
        bayKind: 'preop_bay', stages: ['preop'], weekToggle: false,
        emptyCopy: 'No cases in pre-op in this window.',
    },
    pacu: {
        title: 'PACU',
        subtitle: 'Recovery bays and patients in post-anesthesia care',
        icon: <HeartPulse className="w-5 h-5" />,
        bayKind: 'pacu_bay', stages: ['pacu'], weekToggle: false,
        emptyCopy: 'No patients in PACU in this window.',
    },
};

function localDay(offsetFrom?: string, delta = 0): string {
    const d = offsetFrom ? new Date(`${offsetFrom}T12:00:00`) : new Date();
    d.setDate(d.getDate() + delta);
    return d.toLocaleDateString('en-CA');
}
function labelOf(dateStr: string, style: 'long' | 'short' = 'long'): string {
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString([],
        style === 'long' ? { weekday: 'long', month: 'long', day: 'numeric' } : { weekday: 'short', month: 'short', day: 'numeric' });
}
function hm(ts: string | null): string {
    return ts ? new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';
}

export function StageBoardRoute({ kind }: { kind: BoardKind }) {
    const cfg = CFG[kind];
    const { org_id: orgId } = useOrgContext();
    const [anchor, setAnchor] = useState<string>(localDay());
    const [view, setView] = useState<'day' | 'week'>('day');
    const [provider, setProvider] = useState<string>('all');

    const fromDate = anchor;
    const toDate = view === 'week' && cfg.weekToggle ? localDay(anchor, 6) : anchor;
    const isToday = anchor === localDay();

    const casesQ = useQuery({
        queryKey: ['stage-cases', kind, orgId, fromDate, toDate],
        enabled: !!orgId,
        refetchInterval: 30_000,
        queryFn: async (): Promise<OrCase[]> => {
            const { data, error } = await supabase
                .schema('cr').from('or_cases')
                .select('case_id, case_number, patient_id, patient_name, procedure_name, surgeon_name, or_room, scheduled_start, actual_start, completed_at, estimated_duration_minutes, case_status, schedule_status, anesthesiologist_name, scrub_tech_name, circulating_nurse_name, stage, stage_entered_at, encounter_id, schedule_event_id')
                .eq('org_id', orgId!)
                .gte('case_date', fromDate).lte('case_date', toDate)
                .is('deleted_at', null)
                .order('scheduled_start', { ascending: true });
            if (error) throw error;
            return (data ?? []) as OrCase[];
        },
    });

    const roomsQ = useQuery({
        queryKey: ['stage-rooms', kind, orgId],
        enabled: !!orgId && cfg.bayKind != null,
        queryFn: async (): Promise<RoomResource[]> => {
            const { data, error } = await supabase
                .schema('saas').from('schedule_resources')
                .select('resource_id, resource_kind, display_name')
                .eq('org_id', orgId!).eq('facility_id', ASC_FACILITY_ID)
                .eq('resource_kind', cfg.bayKind!).eq('active', true)
                .order('display_name');
            if (error) throw error;
            return ((data ?? []) as RoomResource[]).sort((a, b) =>
                a.display_name.localeCompare(b.display_name, undefined, { numeric: true, sensitivity: 'base' }));
        },
    });

    const staysQ = useQuery({
        queryKey: ['stage-stays', orgId],
        enabled: !!orgId && cfg.bayKind != null,
        refetchInterval: 30_000,
        queryFn: async (): Promise<RoomStay[]> => {
            const { data, error } = await supabase
                .schema('cr').from('patient_room_stays')
                .select('room_stay_id, encounter_id, room_resource_id, status, assigned_at')
                .eq('org_id', orgId!)
                .in('status', ['occupied', 'cleaning'])
                .order('assigned_at', { ascending: false, nullsFirst: false });
            if (error) throw error;
            const seen = new Set<string>();
            return ((data ?? []) as RoomStay[]).filter((stay) => {
                if (seen.has(stay.room_resource_id)) return false;
                seen.add(stay.room_resource_id);
                return true;
            });
        },
    });

    const all = casesQ.data ?? [];
    const providers = useMemo(
        () => Array.from(new Set(all.map((c) => c.surgeon_name).filter((x): x is string => !!x))).sort(),
        [all]);
    const cases = useMemo(() => {
        let list = all.filter((c) => (c.case_status ?? '') !== 'cancelled' && (c.schedule_status ?? '') !== 'cancelled');
        if (cfg.stages.length && view === 'day' && isToday) list = list.filter((c) => cfg.stages.includes(c.stage ?? ''));
        if (provider !== 'all') list = list.filter((c) => c.surgeon_name === provider);
        return list;
    }, [all, provider, view, isToday, cfg.stages]);

    const stayByResource = new Map((staysQ.data ?? []).map((s) => [s.room_resource_id, s]));
    const caseByEncounter = new Map(all.filter((c) => c.encounter_id != null).map((c) => [c.encounter_id, c]));

    const byDay = useMemo(() => {
        const m = new Map<string, OrCase[]>();
        for (const c of cases) {
            const day = c.scheduled_start ? new Date(c.scheduled_start).toLocaleDateString('en-CA') : fromDate;
            const arr = m.get(day) ?? [];
            arr.push(c); m.set(day, arr);
        }
        return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [cases, fromDate]);

    const err = casesQ.error ?? roomsQ.error ?? staysQ.error;

    return (
        <div className="min-h-full bg-pt-darker">
            <header className="border-b border-pt-border/60 px-6 py-4 flex items-center gap-3 flex-wrap">
                <span className="text-pt-cyan [&>svg]:w-5 [&>svg]:h-5">{cfg.icon}</span>
                <div className="mr-auto">
                    <h1 className="text-lg font-display font-semibold text-pt-text">{cfg.title}</h1>
                    <p className="text-xs text-pt-text-muted">{cfg.subtitle}</p>
                </div>
                {cfg.weekToggle && (
                    <div className="flex rounded border border-pt-input-border overflow-hidden">
                        {(['day', 'week'] as const).map((v) => (
                            <button key={v} type="button" onClick={() => setView(v)}
                                className={['text-xs px-3 py-1.5 capitalize', view === v ? 'bg-pt-cyan/15 text-pt-cyan' : 'text-pt-text-muted hover:text-pt-text'].join(' ')}>
                                {v}
                            </button>
                        ))}
                    </div>
                )}
                <select value={provider} onChange={(e) => setProvider(e.target.value)}
                    className="text-xs rounded border border-pt-input-border bg-pt-darker text-pt-text px-2.5 py-1.5">
                    <option value="all">All providers</option>
                    {providers.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <div className="flex items-center gap-2">
                    <button type="button" aria-label="Previous" onClick={() => setAnchor(localDay(anchor, view === 'week' ? -7 : -1))}
                        className="rounded border border-pt-input-border p-1.5 text-pt-text-muted hover:text-pt-text"><ChevronLeft size={14} /></button>
                    <span className="text-sm text-pt-text min-w-[150px] text-center">
                        {view === 'week' ? `${labelOf(fromDate, 'short')} – ${labelOf(toDate, 'short')}` : labelOf(anchor)}
                    </span>
                    <button type="button" aria-label="Next" onClick={() => setAnchor(localDay(anchor, view === 'week' ? 7 : 1))}
                        className="rounded border border-pt-input-border p-1.5 text-pt-text-muted hover:text-pt-text"><ChevronRight size={14} /></button>
                    {!isToday && (
                        <button type="button" onClick={() => setAnchor(localDay())}
                            className="text-xs text-pt-cyan border border-pt-cyan/40 rounded px-2.5 py-1.5 hover:bg-pt-cyan/10">Today</button>
                    )}
                </div>
            </header>

            <div className="p-6 space-y-6">
                {err != null && (
                    <div className="rounded border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-2.5">
                        {err instanceof Error ? err.message : 'Failed to load'}
                    </div>
                )}

                {cfg.bayKind && (
                    <section>
                        <h2 className="text-xs uppercase tracking-wide text-pt-text-muted mb-2">{kind === 'pacu' ? 'Recovery bays' : 'Pre-op bays'}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {(roomsQ.data ?? []).map((r) => {
                                const stay = stayByResource.get(r.resource_id);
                                const occ = stay?.encounter_id != null ? caseByEncounter.get(stay.encounter_id) : undefined;
                                if (stay?.status === 'occupied') {
                                    return (
                                        <div key={r.resource_id} className="rounded-lg border border-pt-cyan/50 bg-pt-panel px-4 py-3">
                                            <div className="flex items-baseline justify-between">
                                                <span className="text-sm font-medium text-pt-text">{r.display_name}</span>
                                                <span className="text-[10px] tracking-wider text-emerald-400 bg-emerald-500/15 rounded px-1.5 py-0.5">OCCUPIED</span>
                                            </div>
                                            <div className="text-sm text-pt-text mt-1.5 truncate">{occ?.patient_name ?? 'Patient'}</div>
                                            <div className="text-[11px] text-pt-text-muted truncate">{occ?.procedure_name ?? ''}</div>
                                            {occ && careTeamLine(occ) && <div className="text-[10px] text-pt-text-dim truncate mt-0.5">{careTeamLine(occ)}</div>}
                                            <div className="text-[10px] text-pt-text-dim mt-1 flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> since {hm(stay.assigned_at)}
                                            </div>
                                        </div>
                                    );
                                }
                                if (stay?.status === 'cleaning') {
                                    return (
                                        <div key={r.resource_id} className="rounded-lg border border-amber-500/40 bg-pt-panel px-4 py-3">
                                            <div className="flex items-baseline justify-between">
                                                <span className="text-sm font-medium text-pt-text">{r.display_name}</span>
                                                <span className="text-[10px] tracking-wider text-amber-400 bg-amber-500/15 rounded px-1.5 py-0.5">CLEANING</span>
                                            </div>
                                            <div className="text-[11px] text-pt-text-muted mt-1.5">Turnover in progress</div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={r.resource_id} className="rounded-lg border border-dashed border-pt-border bg-transparent px-4 py-3">
                                        <div className="flex items-baseline justify-between">
                                            <span className="text-sm text-pt-text-muted">{r.display_name}</span>
                                            <span className="text-[10px] tracking-wider text-pt-text-dim border border-pt-input-border rounded px-1.5 py-0.5">OPEN</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                <section>
                    <h2 className="text-xs uppercase tracking-wide text-pt-text-muted mb-2">
                        {kind === 'access' ? (view === 'week' ? 'This week' : 'Arrivals') : 'Schedule'}
                    </h2>
                    {cases.length === 0 && (
                        <div className="border border-pt-border rounded-lg bg-pt-panel px-6 py-10 text-center text-sm text-pt-text-muted">
                            {cfg.emptyCopy}
                        </div>
                    )}
                    <div className="space-y-5">
                        {byDay.map(([day, dayCases]) => (
                            <div key={day}>
                                {view === 'week' && <div className="text-xs text-pt-gold mb-1.5">{labelOf(day)}</div>}
                                <ul className="space-y-2">
                                    {dayCases.map((c) => (
                                        <li key={c.case_id}>
                                            <Link to={`/cases/${c.case_id}`}
                                                className="flex items-center gap-4 border border-pt-border rounded-lg bg-pt-panel px-4 py-3 hover:border-pt-cyan/50 transition-colors">
                                                <div className="text-center shrink-0 w-16">
                                                    <div className="text-sm font-medium text-pt-cyan">{hm(c.scheduled_start)}</div>
                                                    <div className="text-[10px] text-pt-text-dim">{c.estimated_duration_minutes ?? '—'} min</div>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium text-pt-text truncate">{c.patient_name ?? `Case #${c.case_id}`}</div>
                                                    <div className="text-[11px] text-pt-text-muted truncate">
                                                        {c.procedure_name ?? 'Procedure TBD'} · {c.surgeon_name ?? 'Surgeon TBD'}
                                                    </div>
                                                    {careTeamLine(c) && <div className="text-[10px] text-pt-text-dim truncate mt-0.5">{careTeamLine(c)}</div>}
                                                </div>
                                                <div className="flex items-center gap-3 text-[11px] text-pt-text-muted shrink-0">
                                                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{c.or_room ?? '—'}</span>
                                                    <span className="uppercase tracking-wide text-[10px] border border-pt-input-border rounded px-1.5 py-0.5">
                                                        {c.stage ?? c.schedule_status ?? '—'}
                                                    </span>
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
